import { supabase } from '../lib/supabase';

export const loyaltyService = {
  // Customer Card Operations
  async getCustomerCard(userId) {
    const { data, error } = await supabase?.from('loyalty_cards')?.select('*')?.eq('user_id', userId)?.single();
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      cardNumber: data?.card_number,
      qrCodeToken: data?.qr_code_token,
      pointsBalance: parseFloat(data?.points_balance),
      totalEarned: parseFloat(data?.total_earned),
      totalSpent: parseFloat(data?.total_spent),
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  async getCustomerTransactions(cardId) {
    const { data, error } = await supabase?.from('loyalty_transactions')?.select(`
        *,
        businesses(business_name)
      `)?.eq('customer_card_id', cardId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(t => ({
      id: t?.id,
      transactionType: t?.transaction_type,
      businessName: t?.businesses?.business_name,
      pointsAmount: parseFloat(t?.points_amount),
      euroAmount: parseFloat(t?.euro_amount),
      balanceBefore: parseFloat(t?.customer_balance_before),
      balanceAfter: parseFloat(t?.customer_balance_after),
      notes: t?.notes,
      createdAt: t?.created_at
    }));
  },

  // Business Operations
  async getBusinessByUserId(userId) {
    const { data, error } = await supabase?.from('businesses')?.select('*')?.eq('user_id', userId)?.single();
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      businessName: data?.business_name,
      businessAddress: data?.business_address,
      businessPhone: data?.business_phone,
      businessWhatsapp: data?.business_whatsapp,
      pointsBalance: parseFloat(data?.points_balance),
      totalPurchased: parseFloat(data?.total_purchased),
      totalLoadedToCustomers: parseFloat(data?.total_loaded_to_customers),
      totalAcceptedFromCustomers: parseFloat(data?.total_accepted_from_customers),
      creditsOwed: parseFloat(data?.credits_owed),
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  async getCardByQRToken(qrToken) {
    const { data, error } = await supabase?.from('loyalty_cards')?.select(`
        *,
        user_profiles(full_name, email)
      `)?.eq('qr_code_token', qrToken)?.single();
    if (error) throw error;
    return {
      id: data?.id,
      userId: data?.user_id,
      cardNumber: data?.card_number,
      pointsBalance: parseFloat(data?.points_balance),
      customerName: data?.user_profiles?.full_name,
      customerEmail: data?.user_profiles?.email,
      isActive: data?.is_active
    };
  },

  async loadPointsToCustomer(businessId, customerCardId, pointsAmount, notes) {
    // Get current balances
    const { data: business } = await supabase?.from('businesses')?.select('points_balance')?.eq('id', businessId)?.single();

    const { data: card } = await supabase?.from('loyalty_cards')?.select('points_balance')?.eq('id', customerCardId)?.single();

    const businessBalanceBefore = parseFloat(business?.points_balance);
    const customerBalanceBefore = parseFloat(card?.points_balance);
    const points = parseFloat(pointsAmount);
    const euros = points * 0.5;

    // Create transaction
    const { error: txError } = await supabase?.from('loyalty_transactions')?.insert({
        transaction_type: 'carico_punti_cliente',
        business_id: businessId,
        customer_card_id: customerCardId,
        points_amount: points,
        euro_amount: euros,
        business_balance_before: businessBalanceBefore,
        business_balance_after: businessBalanceBefore - points,
        customer_balance_before: customerBalanceBefore,
        customer_balance_after: customerBalanceBefore + points,
        notes: notes
      });

    if (txError) throw txError;

    // Update business balance
    await supabase?.from('businesses')?.update({
        points_balance: businessBalanceBefore - points,
        total_loaded_to_customers: business?.total_loaded_to_customers + points
      })?.eq('id', businessId);

    // Update customer balance
    await supabase?.from('loyalty_cards')?.update({
        points_balance: customerBalanceBefore + points,
        total_earned: card?.total_earned + points
      })?.eq('id', customerCardId);
  },

  async acceptPointsFromCustomer(businessId, customerCardId, pointsAmount, notes) {
    // Get current balances
    const { data: business } = await supabase?.from('businesses')?.select('points_balance, credits_owed')?.eq('id', businessId)?.single();

    const { data: card } = await supabase?.from('loyalty_cards')?.select('points_balance')?.eq('id', customerCardId)?.single();

    const businessBalanceBefore = parseFloat(business?.points_balance);
    const customerBalanceBefore = parseFloat(card?.points_balance);
    const points = parseFloat(pointsAmount);
    const euros = points * 0.5;

    // Create transaction
    const { error: txError } = await supabase?.from('loyalty_transactions')?.insert({
        transaction_type: 'scarico_punti_cliente',
        business_id: businessId,
        customer_card_id: customerCardId,
        points_amount: points,
        euro_amount: euros,
        business_balance_before: businessBalanceBefore,
        business_balance_after: businessBalanceBefore + points,
        customer_balance_before: customerBalanceBefore,
        customer_balance_after: customerBalanceBefore - points,
        notes: notes
      });

    if (txError) throw txError;

    // Update business (add points and credits owed)
    await supabase?.from('businesses')?.update({
        points_balance: businessBalanceBefore + points,
        total_accepted_from_customers: business?.total_accepted_from_customers + points,
        credits_owed: parseFloat(business?.credits_owed) + euros
      })?.eq('id', businessId);

    // Update customer balance
    await supabase?.from('loyalty_cards')?.update({
        points_balance: customerBalanceBefore - points,
        total_spent: card?.total_spent + points
      })?.eq('id', customerCardId);
  },

  async getBusinessTransactions(businessId) {
    const { data, error } = await supabase?.from('loyalty_transactions')?.select(`
        *,
        loyalty_cards(card_number, user_profiles(full_name))
      `)?.eq('business_id', businessId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(t => ({
      id: t?.id,
      transactionType: t?.transaction_type,
      customerName: t?.loyalty_cards?.user_profiles?.full_name,
      cardNumber: t?.loyalty_cards?.card_number,
      pointsAmount: parseFloat(t?.points_amount),
      euroAmount: parseFloat(t?.euro_amount),
      balanceBefore: parseFloat(t?.business_balance_before),
      balanceAfter: parseFloat(t?.business_balance_after),
      notes: t?.notes,
      createdAt: t?.created_at
    }));
  },

  // Admin Operations
  async getAllBusinesses() {
    const { data, error } = await supabase?.from('businesses')?.select('*')?.order('business_name');
    if (error) throw error;
    return data?.map(b => ({
      id: b?.id,
      businessName: b?.business_name,
      pointsBalance: parseFloat(b?.points_balance),
      totalPurchased: parseFloat(b?.total_purchased),
      creditsOwed: parseFloat(b?.credits_owed),
      isActive: b?.is_active
    }));
  },

  async topUpBusiness(businessId, pointsAmount, notes) {
    const { data: business } = await supabase?.from('businesses')?.select('points_balance, total_purchased')?.eq('id', businessId)?.single();

    const balanceBefore = parseFloat(business?.points_balance);
    const points = parseFloat(pointsAmount);
    const euros = points * 0.5;

    const { data: { user } } = await supabase?.auth?.getUser();

    // Create transaction
    const { error: txError } = await supabase?.from('loyalty_transactions')?.insert({
        transaction_type: 'top_up_negozio',
        business_id: businessId,
        admin_user_id: user?.id,
        points_amount: points,
        euro_amount: euros,
        business_balance_before: balanceBefore,
        business_balance_after: balanceBefore + points,
        notes: notes
      });

    if (txError) throw txError;

    // Update business balance
    await supabase?.from('businesses')?.update({
        points_balance: balanceBefore + points,
        total_purchased: parseFloat(business?.total_purchased) + points
      })?.eq('id', businessId);
  },

  async getAllTransactions() {
    const { data, error } = await supabase?.from('loyalty_transactions')?.select(`
        *,
        businesses(business_name),
        loyalty_cards(card_number, user_profiles(full_name))
      `)?.order('created_at', { ascending: false })?.limit(100);
    if (error) throw error;
    return data?.map(t => ({
      id: t?.id,
      transactionType: t?.transaction_type,
      businessName: t?.businesses?.business_name,
      customerName: t?.loyalty_cards?.user_profiles?.full_name,
      pointsAmount: parseFloat(t?.points_amount),
      euroAmount: parseFloat(t?.euro_amount),
      createdAt: t?.created_at
    }));
  },

  async getPendingPayouts() {
    const { data, error } = await supabase?.from('business_payouts')?.select(`
        *,
        businesses(business_name)
      `)?.eq('status', 'pending')?.order('requested_at', { ascending: false });
    if (error) throw error;
    return data?.map(p => ({
      id: p?.id,
      businessId: p?.business_id,
      businessName: p?.businesses?.business_name,
      amountEuro: parseFloat(p?.amount_euro),
      pointsAccepted: parseFloat(p?.points_accepted),
      status: p?.status,
      requestedAt: p?.requested_at
    }));
  },

  // Enhanced Payout Management Functions
  async getSettlementHistory(filters = {}) {
    let query = supabase?.from('business_payouts')?.select(`
        *,
        businesses(business_name, business_address, business_phone)
      `);

    if (filters?.businessId) {
      query = query?.eq('business_id', filters?.businessId);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.startDate) {
      query = query?.gte('requested_at', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('requested_at', filters?.endDate);
    }

    query = query?.order('requested_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(p => ({
      id: p?.id,
      businessId: p?.business_id,
      businessName: p?.businesses?.business_name,
      businessAddress: p?.businesses?.business_address,
      businessPhone: p?.businesses?.business_phone,
      amountEuro: parseFloat(p?.amount_euro),
      pointsAccepted: parseFloat(p?.points_accepted),
      status: p?.status,
      requestedAt: p?.requested_at,
      completedAt: p?.completed_at,
      adminNotes: p?.admin_notes
    }));
  },

  async getLedgerAudit(filters = {}) {
    let query = supabase?.from('loyalty_transactions')?.select(`
        *,
        businesses(business_name),
        loyalty_cards(card_number, user_profiles(full_name))
      `);

    if (filters?.businessId) {
      query = query?.eq('business_id', filters?.businessId);
    }
    if (filters?.transactionType) {
      query = query?.eq('transaction_type', filters?.transactionType);
    }
    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }

    query = query?.order('created_at', { ascending: false })?.limit(500);

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(t => ({
      id: t?.id,
      transactionType: t?.transaction_type,
      businessName: t?.businesses?.business_name,
      customerName: t?.loyalty_cards?.user_profiles?.full_name,
      cardNumber: t?.loyalty_cards?.card_number,
      pointsAmount: parseFloat(t?.points_amount),
      euroAmount: parseFloat(t?.euro_amount),
      businessBalanceBefore: parseFloat(t?.business_balance_before),
      businessBalanceAfter: parseFloat(t?.business_balance_after),
      customerBalanceBefore: parseFloat(t?.customer_balance_before),
      customerBalanceAfter: parseFloat(t?.customer_balance_after),
      notes: t?.notes,
      createdAt: t?.created_at
    }));
  },

  async processPayout(payoutId, adminNotes = '') {
    const { data: payout } = await supabase?.from('business_payouts')?.select('*')?.eq('id', payoutId)?.single();

    if (!payout) throw new Error('Payout non trovato');
    if (payout?.status !== 'pending') throw new Error('Payout già processato');

    const { data: business } = await supabase?.from('businesses')?.select('credits_owed')?.eq('id', payout?.business_id)?.single();

    const { data: { user } } = await supabase?.auth?.getUser();

    // Create payout transaction in ledger
    const { error: txError } = await supabase?.from('loyalty_transactions')?.insert({
        transaction_type: 'payout_negozio',
        business_id: payout?.business_id,
        admin_user_id: user?.id,
        points_amount: parseFloat(payout?.points_accepted),
        euro_amount: parseFloat(payout?.amount_euro),
        business_balance_before: null,
        business_balance_after: null,
        notes: `Rimborso completato - ${adminNotes || 'Pagamento effettuato'}`
      });

    if (txError) throw txError;

    // Update payout status
    await supabase?.from('business_payouts')?.update({
        status: 'completed',
        completed_at: new Date()?.toISOString(),
        admin_notes: adminNotes
      })?.eq('id', payoutId);

    // Update business credits owed
    await supabase?.from('businesses')?.update({
        credits_owed: parseFloat(business?.credits_owed) - parseFloat(payout?.amount_euro)
      })?.eq('id', payout?.business_id);
  },

  async processBulkPayouts(payoutIds, adminNotes = '') {
    const results = [];
    for (const payoutId of payoutIds) {
      try {
        await this.processPayout(payoutId, adminNotes);
        results?.push({ payoutId, success: true });
      } catch (error) {
        results?.push({ payoutId, success: false, error: error?.message });
      }
    }
    return results;
  },

  async cancelPayout(payoutId, reason = '') {
    const { data: payout } = await supabase?.from('business_payouts')?.select('*')?.eq('id', payoutId)?.single();

    if (!payout) throw new Error('Payout non trovato');
    if (payout?.status !== 'pending') throw new Error('Payout già processato');

    await supabase?.from('business_payouts')?.update({
        status: 'cancelled',
        admin_notes: reason
      })?.eq('id', payoutId);
  },

  async getPayoutStats() {
    const { data: pending } = await supabase?.from('business_payouts')?.select('amount_euro')?.eq('status', 'pending');

    const { data: completed } = await supabase?.from('business_payouts')?.select('amount_euro')?.eq('status', 'completed');

    const totalPending = pending?.reduce((sum, p) => sum + parseFloat(p?.amount_euro), 0) || 0;
    const totalCompleted = completed?.reduce((sum, p) => sum + parseFloat(p?.amount_euro), 0) || 0;

    return {
      totalPending,
      totalCompleted,
      pendingCount: pending?.length || 0,
      completedCount: completed?.length || 0
    };
  },

  // New method for manual payout processing
  async processPayoutManual(businessId, amountEuro, notes) {
    const { data: business } = await supabase?.from('businesses')?.select('credits_owed')?.eq('id', businessId)?.single();

    if (!business) throw new Error('Negozio non trovato');
    
    const creditsOwed = parseFloat(business?.credits_owed);
    const paymentAmount = parseFloat(amountEuro);

    if (paymentAmount > creditsOwed) {
      throw new Error('Importo pagamento superiore al credito dovuto');
    }

    const { data: { user } } = await supabase?.auth?.getUser();

    // Create payout transaction in ledger
    const { error: txError } = await supabase?.from('loyalty_transactions')?.insert({
        transaction_type: 'payout_negozio',
        business_id: businessId,
        admin_user_id: user?.id,
        points_amount: paymentAmount / 0.5,
        euro_amount: paymentAmount,
        business_balance_before: null,
        business_balance_after: null,
        notes: notes
      });

    if (txError) throw txError;

    // Update business credits owed
    await supabase?.from('businesses')?.update({
        credits_owed: creditsOwed - paymentAmount
      })?.eq('id', businessId);
  },

  // New method for monthly report generation
  async getMonthlyReport(businessId, monthYear) {
    // monthYear format: 'YYYY-MM'
    const startDate = `${monthYear}-01T00:00:00Z`;
    const endDate = new Date(monthYear + '-01');
    endDate?.setMonth(endDate?.getMonth() + 1);
    const endDateStr = endDate?.toISOString();

    const { data: business } = await supabase?.from('businesses')?.select('business_name, points_balance')?.eq('id', businessId)?.single();

    if (!business) throw new Error('Negozio non trovato');

    // Get all transactions for the month
    const { data: transactions } = await supabase?.from('loyalty_transactions')?.select('*')?.eq('business_id', businessId)?.gte('created_at', startDate)?.lt('created_at', endDateStr);

    const pointsPurchased = transactions?.filter(t => t?.transaction_type === 'top_up_negozio')?.reduce((sum, t) => sum + parseFloat(t?.points_amount), 0) || 0;

    const pointsIssued = transactions?.filter(t => t?.transaction_type === 'carico_punti_cliente')?.reduce((sum, t) => sum + parseFloat(t?.points_amount), 0) || 0;

    const pointsRedeemed = transactions?.filter(t => t?.transaction_type === 'scarico_punti_cliente')?.reduce((sum, t) => sum + parseFloat(t?.points_amount), 0) || 0;

    const eurosPaid = transactions?.filter(t => t?.transaction_type === 'payout_negozio')?.reduce((sum, t) => sum + parseFloat(t?.euro_amount), 0) || 0;

    const eurosToPay = pointsRedeemed * 0.5;
    const eurosBalance = eurosToPay - eurosPaid;

    return {
      businessName: business?.business_name,
      monthYear,
      pointsPurchased,
      pointsIssued,
      pointsRedeemed,
      pointsBalance: parseFloat(business?.points_balance),
      eurosToPay,
      eurosPaid,
      eurosBalance,
      transactionCount: transactions?.length || 0
    };
  },

  // New method for transaction reversal
  async reversalTransaction(transactionId, reason) {
    if (!reason?.trim()) {
      throw new Error('La motivazione dello storno è obbligatoria');
    }

    const { data: originalTx } = await supabase?.from('loyalty_transactions')?.select('*')?.eq('id', transactionId)?.single();

    if (!originalTx) throw new Error('Transazione non trovata');

    // Check if already reversed
    const { data: existingReversal } = await supabase?.from('loyalty_transactions')?.select('id')?.eq('transaction_type', 'reversal')?.ilike('notes', `%Storno di transazione ${transactionId}%`)?.single();

    if (existingReversal) {
      throw new Error('Questa transazione è già stata stornata');
    }

    const { data: { user } } = await supabase?.auth?.getUser();

    // Create reversal transaction (inverse of original)
    const reversalNotes = `Storno di transazione ${transactionId} - Tipo: ${originalTx?.transaction_type} - Motivazione: ${reason}`;

    const { error: reversalError } = await supabase?.from('loyalty_transactions')?.insert({
        transaction_type: 'reversal',
        business_id: originalTx?.business_id,
        customer_card_id: originalTx?.customer_card_id,
        admin_user_id: user?.id,
        points_amount: -parseFloat(originalTx?.points_amount),
        euro_amount: -parseFloat(originalTx?.euro_amount),
        business_balance_before: null,
        business_balance_after: null,
        customer_balance_before: null,
        customer_balance_after: null,
        notes: reversalNotes
      });

    if (reversalError) throw reversalError;

    // Reverse the balances based on transaction type
    if (originalTx?.transaction_type === 'top_up_negozio') {
      // Reverse top-up: subtract points from business
      const { data: business } = await supabase?.from('businesses')?.select('points_balance, total_purchased')?.eq('id', originalTx?.business_id)?.single();
      
      await supabase?.from('businesses')?.update({
          points_balance: parseFloat(business?.points_balance) - parseFloat(originalTx?.points_amount),
          total_purchased: parseFloat(business?.total_purchased) - parseFloat(originalTx?.points_amount)
        })?.eq('id', originalTx?.business_id);
    } else if (originalTx?.transaction_type === 'carico_punti_cliente') {
      // Reverse customer load: add points back to business, subtract from customer
      const { data: business } = await supabase?.from('businesses')?.select('points_balance, total_loaded_to_customers')?.eq('id', originalTx?.business_id)?.single();
      
      const { data: card } = await supabase?.from('loyalty_cards')?.select('points_balance, total_earned')?.eq('id', originalTx?.customer_card_id)?.single();

      await supabase?.from('businesses')?.update({
          points_balance: parseFloat(business?.points_balance) + parseFloat(originalTx?.points_amount),
          total_loaded_to_customers: parseFloat(business?.total_loaded_to_customers) - parseFloat(originalTx?.points_amount)
        })?.eq('id', originalTx?.business_id);

      await supabase?.from('loyalty_cards')?.update({
          points_balance: parseFloat(card?.points_balance) - parseFloat(originalTx?.points_amount),
          total_earned: parseFloat(card?.total_earned) - parseFloat(originalTx?.points_amount)
        })?.eq('id', originalTx?.customer_card_id);
    } else if (originalTx?.transaction_type === 'scarico_punti_cliente') {
      // Reverse customer redemption: subtract points from business, add back to customer, reduce credits owed
      const { data: business } = await supabase?.from('businesses')?.select('points_balance, total_accepted_from_customers, credits_owed')?.eq('id', originalTx?.business_id)?.single();
      
      const { data: card } = await supabase?.from('loyalty_cards')?.select('points_balance, total_spent')?.eq('id', originalTx?.customer_card_id)?.single();

      await supabase?.from('businesses')?.update({
          points_balance: parseFloat(business?.points_balance) - parseFloat(originalTx?.points_amount),
          total_accepted_from_customers: parseFloat(business?.total_accepted_from_customers) - parseFloat(originalTx?.points_amount),
          credits_owed: parseFloat(business?.credits_owed) - parseFloat(originalTx?.euro_amount)
        })?.eq('id', originalTx?.business_id);

      await supabase?.from('loyalty_cards')?.update({
          points_balance: parseFloat(card?.points_balance) + parseFloat(originalTx?.points_amount),
          total_spent: parseFloat(card?.total_spent) - parseFloat(originalTx?.points_amount)
        })?.eq('id', originalTx?.customer_card_id);
    } else if (originalTx?.transaction_type === 'payout_negozio') {
      // Reverse payout: add credits back to business
      const { data: business } = await supabase?.from('businesses')?.select('credits_owed')?.eq('id', originalTx?.business_id)?.single();
      
      await supabase?.from('businesses')?.update({
          credits_owed: parseFloat(business?.credits_owed) + parseFloat(originalTx?.euro_amount)
        })?.eq('id', originalTx?.business_id);
    }
  }
};